import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { JevBudgetView, RaiseJevBudgetInput, SetJevBudgetInput } from '@nest-recruit/shared';
import { apiGet, apiPost, apiPut, errorMessage, type ApiError } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { jevKey } from './jev-api';

// ADR 0070 — a job's Jev budget on the Ranking page. Under Jev's own key, so the server's hint after a Jev read or a budget change
// refetches it with no polling (#12).

export function jevBudgetKey(jobId: string): readonly unknown[] {
  return [...jevKey(jobId), 'budget'];
}

export function useJevBudget(jobId: string): UseQueryResult<JevBudgetView, ApiError> {
  return useQuery<JevBudgetView, ApiError>({
    queryKey: jevBudgetKey(jobId),
    queryFn: () => apiGet<JevBudgetView>(`/jobs/${jobId}/jev-budget`),
    placeholderData: (previous) => previous,
  });
}

/** Sets a first budget or raises it: the amount shows at once, and comes back only if the server refuses (CLAUDE.md §5a rule 1). */
function useChangeJevBudget(jobId: string, send: (input: SetJevBudgetInput) => Promise<JevBudgetView>, failure: string) {
  const queryClient = useQueryClient();
  const showToast = useToasts((state) => state.show);
  return useMutation<JevBudgetView, ApiError, SetJevBudgetInput, { readonly previous: JevBudgetView | undefined }>({
    mutationFn: send,
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: jevBudgetKey(jobId) });
      const previous = queryClient.getQueryData<JevBudgetView>(jevBudgetKey(jobId));
      if (previous) queryClient.setQueryData<JevBudgetView>(jevBudgetKey(jobId), { ...previous, budgetMicroUsd: input.budgetMicroUsd, canSet: false });
      return { previous };
    },
    onError: (error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(jevBudgetKey(jobId), context.previous);
      showToast({ tone: 'danger', message: failure, detail: errorMessage(error) });
    },
    onSuccess: (view) => queryClient.setQueryData(jevBudgetKey(jobId), view),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: jevBudgetKey(jobId) }),
  });
}

export function useSetJevBudget(jobId: string) {
  return useChangeJevBudget(jobId, (input) => apiPut<JevBudgetView>(`/jobs/${jobId}/jev-budget`, input), 'The Jev budget was not set');
}

export function useRaiseJevBudget(jobId: string) {
  return useChangeJevBudget(jobId, (input: RaiseJevBudgetInput) => apiPost<JevBudgetView>(`/jobs/${jobId}/jev-budget/raise`, input), 'The Jev budget was not raised');
}

/** Stage 3: a recruiter asks the HR Lead for more. The button shows "Asked" at once and goes back only if the server refuses (CLAUDE.md §5a rule 1). */
export function useAskJevBudget(jobId: string) {
  const queryClient = useQueryClient();
  const showToast = useToasts((state) => state.show);
  return useMutation<JevBudgetView, ApiError, void, { readonly previous: JevBudgetView | undefined }>({
    mutationFn: () => apiPost<JevBudgetView>(`/jobs/${jobId}/jev-budget/ask`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: jevBudgetKey(jobId) });
      const previous = queryClient.getQueryData<JevBudgetView>(jevBudgetKey(jobId));
      if (previous) queryClient.setQueryData<JevBudgetView>(jevBudgetKey(jobId), { ...previous, openAsk: { at: new Date().toISOString(), byName: null } });
      return { previous };
    },
    onError: (error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(jevBudgetKey(jobId), context.previous);
      showToast({ tone: 'danger', message: 'HR was not asked', detail: errorMessage(error) });
    },
    onSuccess: (view) => queryClient.setQueryData(jevBudgetKey(jobId), view),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: jevBudgetKey(jobId) }),
  });
}
